import { useState, useEffect, useCallback } from 'react';

const isStandaloneDisplay = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true; // iOS Safari's own flag

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

/**
 * Wraps the browser install-prompt lifecycle.
 *
 * - Android/Chrome/desktop Chrome: the browser fires `beforeinstallprompt`
 *   when its own install criteria are met. We capture that event so we can
 *   trigger it later from our own UI instead of Chrome's mini-infobar.
 * - iOS Safari never fires `beforeinstallprompt` - there's no programmatic
 *   install API at all. `platform === 'ios'` lets the UI show manual
 *   "Share -> Add to Home Screen" instructions instead.
 * - `isInstalled` is derived from actual display-mode, not a stored flag -
 *   so it's always correct even across reinstalls/uninstalls.
 */
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(isStandaloneDisplay());
  const [platform, setPlatform] = useState('other');

  useEffect(() => {
    setPlatform(isIOS() ? 'ios' : 'other');

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };
    const handleDisplayModeChange = (e) => setIsInstalled(e.matches);

    const mql = window.matchMedia('(display-mode: standalone)');

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    mql.addEventListener?.('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mql.removeEventListener?.('change', handleDisplayModeChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice.outcome === 'accepted') setIsInstalled(true);
    return choice;
  }, [deferredPrompt]);

  return {
    isInstalled,
    platform, // 'ios' | 'other'
    canPromptNatively: !!deferredPrompt,
    promptInstall,
  };
}
